/**
 * Живой уровень звука во время записи (решение владельца §5.3, сверка §3.3 п. 3).
 *
 * 🔴 ЗАЧЕМ. «Ученик должен видеть, что его слышно». До сих пор во время записи на
 * экране было только время: микрофон мог не слышать ничего, часы шли, и человек
 * узнавал об этом через две минуты — когда сервер отвергал тишину.
 *
 * ⚠️ СРЕДНЕКВАДРАТИЧНОЕ, А НЕ ПИК. Один щелчок не должен рисовать полную шкалу
 * (иначе «тебя слышно» загоралось бы от стука по столу при мёртвом микрофоне), а
 * ровный тихий голос не должен оставлять её пустой.
 */

/** Ниже этого считаем, что звука нет вовсе: мёртвый микрофон отдаёт мелкий шум оцифровки. */
export const LEVEL_SILENT = 0.01;

/** Сколько тишины подряд, прежде чем сказать «микрофон не слышит». */
export const SILENCE_ALERT_MS = 3000;

/**
 * Подсказки во время записи (14.09.2026): «Мы вас не слышим» и «Говорите громче».
 *
 * 🔴 ЗАЧЕМ. Разбор записей 13–14.09: из 144 отказов сервера 108 — тишина в файле.
 * Беды две, и лечатся они по-разному: микрофон не работает вовсе (нет разрешения,
 * занят, выключен) — и человек далеко или шепчет. Различаем по уровню.
 *
 * ⚠️ «РОВНО НОЛЬ» СЧИТАЕТСЯ ТОЛЬКО ПО ТОЧНЫМ ОТСЧЁТАМ. Одна ступень байтовых
 * отсчётов — 1/128, это −42 дБFS: шум живого микрофона в тихой комнате (−60…−70)
 * там округляется до нуля, и рабочий микрофон сошёл бы за мёртвый. Точные
 * (`getFloatTimeDomainData`) отдают цифровой ноль только у микрофона, который не
 * отдаёт звук вовсе, — у таких записей на сервере пик −120 дБ.
 */
/** Ниже −100 дБFS — звука нет вовсе. У рабочего микрофона шум выше −90. */
export const LEVEL_ZERO = 1e-5;
/**
 * Ниже −45 дБFS — звук есть, но слабый. Сервер считает голосом −50 дБFS
 * (`record-integrity.md` §4); клиент строже на 5 дБ, чтобы «громче» прозвучало
 * раньше, чем сервер скажет «в записи не слышно голоса».
 */
export const LEVEL_WEAK = 0.0056;
/** Столько замеров подряд с нормальным звуком гасят подсказку: один щелчок — не голос. */
export const HINT_CLEAR_SAMPLES = 2;

export type RecordHint = 'none' | 'quiet' | null;

/**
 * Какую подсказку показывать сейчас.
 *
 * ⚠️ БЕЗ МИГАНИЯ. Показ — только после трёх секунд подряд ниже порога; гаснет —
 * от двух нормальных замеров подряд; одиночный громкий замер показанную подсказку
 * не снимает. Переход «не слышим» → «громче» (появился шум) — без пустого кадра.
 */
export function createHintRun() {
  let quietSince: number | null = null;
  let zeroSince: number | null = null;
  let loud = 0;
  let shown: RecordHint = null;
  return {
    /** `precise` — отсчёты точные; по байтовым «ровно ноль» не отличить от тишины. */
    push(level: number, now: number, precise: boolean): RecordHint {
      if (level >= LEVEL_WEAK) {
        loud += 1;
        quietSince = null;
        zeroSince = null;
        if (loud >= HINT_CLEAR_SAMPLES) shown = null;
        return shown;
      }
      loud = 0;
      if (quietSince == null) quietSince = now;
      if (precise && level < LEVEL_ZERO) {
        if (zeroSince == null) zeroSince = now;
      } else {
        zeroSince = null;
      }
      if (zeroSince != null && now - zeroSince >= SILENCE_ALERT_MS) shown = 'none';
      else if (now - quietSince >= SILENCE_ALERT_MS) shown = 'quiet';
      return shown;
    },
  };
}

/**
 * Уровень 0…1 → доля шкалы, по децибелам: −60 дБFS пусто, 0 дБFS полная.
 *
 * ⚠️ ЛИНЕЙНАЯ ШКАЛА И БЫЛА «ЕЛЕ ЗАМЕТНОЙ». Обычный голос у телефона — RMS 0,03…0,1,
 * то есть 3–10 % ширины: владелец на iPhone 14.09 полосу почти не увидел.
 */
export function levelToScale(level: number): number {
  if (!(level > 0)) return 0;
  const db = 20 * Math.log10(level);
  return Math.max(0, Math.min(1, (db + 60) / 60));
}

/** Уровень 0…1 из точных отсчётов `getFloatTimeDomainData` (−1…1). */
export function levelFromFloat(data: Float32Array): number {
  if (!data.length) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
  return Math.min(1, Math.sqrt(sum / data.length));
}

/**
 * Уровень 0…1 из отсчётов `AnalyserNode.getByteTimeDomainData`.
 *
 * В этих данных тишина — это 128 (середина шкалы байта), а не ноль.
 */
export function levelFromTimeDomain(data: Uint8Array): number {
  if (!data.length) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    const v = (data[i] - 128) / 128;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / data.length));
}

export type SilenceRun = {
  /** Отдать очередной уровень и узнать, сколько тишина длится подряд, в мс. */
  push(level: number, now: number): number;
};

/**
 * Сколько тишина длится подряд.
 *
 * ⚠️ ЗВУК СБРАСЫВАЕТ СЧЁТЧИК. Пауза между словами — это не «микрофон не слышит»,
 * и предупреждать о ней нельзя: ученик перестанет верить предупреждениям вовсе.
 */
export function createSilenceRun(): SilenceRun {
  let since: number | null = null;
  return {
    push(level, now) {
      if (level > LEVEL_SILENT) {
        since = null;
        return 0;
      }
      if (since == null) {
        since = now;
        return 0;
      }
      return Math.max(0, now - since);
    },
  };
}

type AnalyserLike = {
  fftSize: number;
  getByteTimeDomainData(data: Uint8Array): void;
  getFloatTimeDomainData?(data: Float32Array): void;
};
type SourceLike = { connect(node: AnalyserLike): void; disconnect?(): void };
type AudioCtxLike = {
  createMediaStreamSource(stream: MediaStream): SourceLike;
  createAnalyser(): AnalyserLike;
  close?(): void | Promise<void>;
  state?: string;
  resume?(): Promise<void> | void;
};

export type MicMeter = {
  /** Текущий уровень 0…1. Спрашивается из кадра отрисовки, поэтому не реактивен. */
  level(): number;
  /** Сколько тишина длится подряд, в мс. */
  silentMs(now: number): number;
  /** Отсчёты точные (float) — по ним можно судить о «ровно ноль». */
  precise: boolean;
  /**
   * Звуковой контекст действительно работает.
   *
   * ⚠️ Спящий контекст (Safari после `await`) отдаёт нули. Это не ноль микрофона,
   * и «Мы вас не слышим» по нему было бы ложью.
   */
  live(): boolean;
  stop(): void;
};

/**
 * Подключить измеритель к живому потоку.
 *
 * ⚠️ ВОЗВРАЩАЕТ `null`, А НЕ БРОСАЕТ. Измеритель — украшение поверх записи: если
 * в браузере нет WebAudio или контекст не открылся, запись обязана идти дальше.
 * Второй потребитель того же потока — сам по себе риск (12.09.2026 проверено на
 * Safari и Chrome: длина не теряется), и падать из-за него нельзя тем более.
 */
export function attachMicLevel(
  stream: MediaStream,
  make?: () => AudioCtxLike | null,
): MicMeter | null {
  const factory =
    make ??
    (() => {
      const w = globalThis as unknown as {
        AudioContext?: new () => AudioCtxLike;
        webkitAudioContext?: new () => AudioCtxLike;
      };
      const Ctx = w.AudioContext ?? w.webkitAudioContext;
      return Ctx ? new Ctx() : null;
    });

  let ctx: AudioCtxLike | null = null;
  try {
    ctx = factory();
    if (!ctx) return null;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const precise = typeof analyser.getFloatTimeDomainData === 'function';
    const floats = precise ? new Float32Array(analyser.fftSize) : null;
    const run = createSilenceRun();
    let last = 0;
    let stopped = false;
    const opened = ctx;
    if (opened.state === 'suspended') {
      try {
        void Promise.resolve(opened.resume?.()).catch(() => undefined);
      } catch {
        /* разбудить не вышло — подсказки просто промолчат */
      }
    }

    return {
      precise,
      level() {
        if (stopped) return 0;
        try {
          if (floats && analyser.getFloatTimeDomainData) {
            analyser.getFloatTimeDomainData(floats);
            last = levelFromFloat(floats);
          } else {
            analyser.getByteTimeDomainData(data);
            last = levelFromTimeDomain(data);
          }
        } catch {
          last = 0;
        }
        return last;
      },
      silentMs(now) {
        return stopped ? 0 : run.push(last, now);
      },
      live() {
        return !stopped && (opened.state == null || opened.state === 'running');
      },
      stop() {
        if (stopped) return;
        stopped = true;
        try {
          source.disconnect?.();
        } catch {
          /* уже отключено */
        }
        try {
          void ctx?.close?.();
        } catch {
          /* закрывать нечего */
        }
      },
    };
  } catch {
    try {
      void ctx?.close?.();
    } catch {
      /* закрывать нечего */
    }
    return null;
  }
}
